// Burst chart (owned by the web:charts agent).
//
// Loss rarely arrives one packet at a time. This shows the run-length
// distribution of consecutive losses (canvas bars), plus the mean and longest
// burst and the Gilbert-Elliott p/q transition estimates that summarise how
// "sticky" the bad state is. A high q with low p means short, frequent blips;
// low q means once loss starts it lingers.

import { useCallback, useMemo } from 'react';
import type { PacketClass, RunResult } from '@/engine/types';
import { CanvasSurface, EmptyNote } from './Canvas';
import { useChartTheme, withAlpha, type ChartPalette } from './palette';

export interface BurstChartProps {
  run: RunResult;
  height?: number;
}

const LOSS_CLASSES: ReadonlySet<PacketClass> = new Set<PacketClass>([
  'lostUp',
  'lostDown',
  'unknown',
]);

/** Run lengths of consecutive lost packets, indexed by length-1. */
function runLengths(run: RunResult): number[] {
  const counts: number[] = [];
  let cur = 0;
  const flush = () => {
    if (cur > 0) {
      counts[cur - 1] = (counts[cur - 1] ?? 0) + 1;
      cur = 0;
    }
  };
  for (const p of run.packets) {
    if (p.cls === 'notSent') continue;
    if (LOSS_CLASSES.has(p.cls)) cur += 1;
    else flush();
  }
  flush();
  for (let i = 0; i < counts.length; i++) counts[i] = counts[i] ?? 0;
  return counts;
}

export function BurstChart({ run, height = 180 }: BurstChartProps) {
  const { palette } = useChartTheme();
  const counts = useMemo(() => runLengths(run), [run]);
  const b = run.metrics.burst;
  const totalBursts = counts.reduce((a, c) => a + c, 0);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) =>
      drawBurstHist(ctx, w, h, counts, palette),
    [counts, palette],
  );

  return (
    <figure style={{ margin: 0 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          fontSize: 12,
          color: palette.text,
          marginBottom: 6,
        }}
      >
        <Stat label="Bursts" value={String(totalBursts)} palette={palette} />
        <Stat label="Mean length" value={b.meanLen.toFixed(2)} palette={palette} />
        <Stat label="Longest" value={String(b.longest)} palette={palette} />
        <Stat label="≥3 fraction" value={`${(b.fracInBurstsGte3 * 100).toFixed(1)}%`} palette={palette} />
        <Stat label="G-E p (good→bad)" value={b.ge_p.toFixed(4)} palette={palette} />
        <Stat label="G-E q (bad→good)" value={b.ge_q.toFixed(4)} palette={palette} />
      </div>
      {totalBursts === 0 ? (
        <EmptyNote label="No loss bursts — clean run." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <CanvasSurface height={height} draw={draw} ariaLabel="Loss burst run-length histogram" />
        </div>
      )}
    </figure>
  );
}

function Stat({
  label,
  value,
  palette,
}: {
  label: string;
  value: string;
  palette: ChartPalette;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        padding: '4px 8px',
        background: palette.panel,
        borderRadius: 4,
        minWidth: 72,
      }}
    >
      <span style={{ color: palette.textMuted, fontSize: 11 }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{value}</span>
    </span>
  );
}

function drawBurstHist(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  counts: number[],
  palette: ChartPalette,
) {
  if (counts.length === 0) return;
  const padB = 18;
  const padL = 26;
  const plotH = h - padB;
  const plotW = w - padL;
  const maxCount = Math.max(1, ...counts);
  const bw = plotW / counts.length;

  ctx.strokeStyle = palette.grid;
  ctx.beginPath();
  ctx.moveTo(padL, plotH);
  ctx.lineTo(padL + plotW, plotH);
  ctx.stroke();

  ctx.font = '10px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  counts.forEach((c, i) => {
    const x = padL + i * bw;
    const bh = (c / maxCount) * (plotH - 4);
    ctx.fillStyle = withAlpha(palette.lostUp, 0.55 + 0.45 * (i / Math.max(1, counts.length - 1)));
    ctx.fillRect(x + 1, plotH - bh, Math.max(1, bw - 2), bh);
    if (bw >= 16) {
      ctx.fillStyle = palette.textMuted;
      ctx.textAlign = 'center';
      ctx.fillText(String(i + 1), x + bw / 2, plotH + 3);
      if (c > 0) {
        ctx.fillStyle = palette.text;
        ctx.fillText(String(c), x + bw / 2, plotH - bh - 12);
      }
    }
  });

  ctx.fillStyle = palette.textMuted;
  ctx.textAlign = 'left';
  ctx.save();
  ctx.translate(8, plotH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.fillText('count', 0, 0);
  ctx.restore();
  ctx.textAlign = 'center';
  ctx.fillText('burst length (packets)', padL + plotW / 2, h - 10);
}
